import api from "./api";

/** One period of one fee, as the parent sees it. */
export type DueCharge = {
  id: string;
  club_id: string;
  club_name: string;
  athlete_id: string;
  athlete_name: string | null;
  product_name: string;
  period_label: string;
  period_start: string;
  period_end: string;
  due_date: string;
  /** The club's list price, before any discount. */
  amount_cents: number;
  discount_cents: number;
  /** Why it costs less: "Reducere frați 20%", "Bursă: ...". */
  discount_note: string | null;
  total_cents: number;
  paid_cents: number;
  outstanding_cents: number;
  currency: string;
  status: string;
  /** Negative once it is late. */
  days_until_due: number;
  /**
   * The agreed slices, when the club has put this period on a payment plan.
   * With a plan these are what the parent pays; the period is never offered as
   * a lump, which is the point of having one.
   */
  instalments: Instalment[];
};

export type DueClub = {
  club_id: string;
  club_name: string;
  can_pay_online: boolean;
  blocked_reason: string | null;
  blocked_message: string | null;
};

export type PaidCharge = {
  id: string;
  club_name: string;
  athlete_name: string | null;
  product_name: string;
  period_label: string;
  amount_cents: number;
  discount_cents: number;
  discount_note: string | null;
  total_cents: number;
  currency: string;
  paid_at: string | null;
  payment_method: string | null;
  /** The club waived this period — nothing was paid and nothing is owed. */
  waived: boolean;
};

export type AttemptState = {
  id: string;
  status: "created" | "succeeded" | "failed" | "expired" | "canceled";
  amount_cents: number;
  currency: string;
  failure_message: string | null;
};

export async function getDuePayments(): Promise<{ charges: DueCharge[]; clubs: DueClub[] }> {
  const { data } = await api.get<{ charges: DueCharge[]; clubs: DueClub[] }>("/payments/due");
  return { charges: data.charges || [], clubs: data.clubs || [] };
}

export async function getPaymentHistory(): Promise<PaidCharge[]> {
  const { data } = await api.get<{ charges: PaidCharge[] }>("/payments/history");
  return data.charges || [];
}

/**
 * Hand off to Stripe Checkout. Returns the URL for the caller to navigate to —
 * nothing is settled here, and nothing is settled when the parent comes back
 * either: the webhook does that.
 */
export async function startChargeCheckout(input: {
  chargeIds: string[];
  /** Slices chosen off a payment plan. The periods they belong to are derived. */
  instalmentIds?: string[];
  saveCard?: boolean;
  returnPath?: string;
}): Promise<{ url: string; attempt_id: string }> {
  const { data } = await api.post<{ url: string; attempt_id: string }>("/payments/checkout", {
    charge_ids: input.chargeIds,
    ...(input.instalmentIds && input.instalmentIds.length ? { instalment_ids: input.instalmentIds } : {}),
    save_card: input.saveCard === true,
    ...(input.returnPath ? { return_path: input.returnPath } : {}),
  });
  return data;
}

export async function getAttemptState(attemptId: string): Promise<AttemptState> {
  const { data } = await api.get<AttemptState>(`/payments/attempt/${attemptId}`);
  return data;
}

export function formatMoney(cents: number, currency = "RON"): string {
  return `${(cents / 100).toFixed(2)} ${currency}`;
}

/** A card left with one club, and the permission that goes with it. */
export type Mandate = {
  id: string;
  club_id: string;
  club_name: string;
  athlete_name: string | null;
  card_brand: string | null;
  card_last4: string | null;
  card_exp_month: number | null;
  card_exp_year: number | null;
  status: "active" | "paused" | "revoked";
  consented_at: string;
  last_charged_at: string | null;
};

export async function getMandates(): Promise<Mandate[]> {
  const { data } = await api.get<{ mandates: Mandate[] }>("/payments/mandates");
  return data.mandates || [];
}

/** Save a card without paying anything — Stripe Checkout in setup mode. */
export async function startMandateSetup(clubId: string, returnPath?: string): Promise<{ url: string }> {
  const { data } = await api.post<{ url: string }>("/payments/mandates", {
    club_id: clubId,
    ...(returnPath ? { return_path: returnPath } : {}),
  });
  return data;
}

export async function revokeMandate(id: string): Promise<void> {
  await api.delete(`/payments/mandates/${id}`);
}

/** One of a coach's teams, as a payment headline. */
export type CoachTeamPayments = {
  team_id: string;
  team_name: string;
  age_group: string | null;
  athletes: number;
  settled: number;
  owing: number;
  overdue: number;
  outstanding_cents: number;
};

export type TeamAthletePayments = {
  athlete_id: string;
  athlete_name: string | null;
  state: "settled" | "owing" | "overdue";
  outstanding_cents: number;
  days_late: number;
  periods: {
    id: string;
    product_name: string;
    period_label: string;
    outstanding_cents: number;
    due_date: string;
    overdue: boolean;
  }[];
};

export type ClubCollections = {
  month: { collected_cents: number; cash_cents: number; card_cents: number };
  outstanding_cents: number;
  overdue_cents: number;
  open_periods: number;
  overdue_periods: number;
  athletes_owing: number;
  worst: {
    athlete_id: string;
    athlete_name: string | null;
    team_name: string | null;
    outstanding_cents: number;
    periods: number;
    days_late: number;
  }[];
};

export async function getCoachTeamPayments(): Promise<CoachTeamPayments[]> {
  const { data } = await api.get<{ teams: CoachTeamPayments[] }>("/payment-reports/coach/teams");
  return data.teams || [];
}

export async function getTeamPaymentDetail(
  teamId: string
): Promise<{ team_name: string; athletes: TeamAthletePayments[] }> {
  const { data } = await api.get<{ team_name: string; athletes: TeamAthletePayments[] }>(
    `/payment-reports/coach/team/${teamId}`
  );
  return { team_name: data.team_name, athletes: data.athletes || [] };
}

export async function getClubCollections(clubId: string): Promise<ClubCollections> {
  const { data } = await api.get<ClubCollections>(`/payment-reports/club/${clubId}`);
  return data;
}

/** One online payment the club received, from the ledger. */
export type ClubPayment = {
  ledger_id: string;
  occurred_at: string;
  gross_cents: number;
  provider_fee_cents: number;
  net_cents: number;
  currency: string;
  refunded_cents: number;
  /** What a refund may still take back — 0 once it has all gone. */
  refundable_cents: number;
  payer_name: string | null;
  athlete_name: string | null;
  periods: number;
  initiated_by: string;
  receipt_id: string | null;
  receipt_number: string | null;
};

export async function getClubPayments(clubId: string): Promise<ClubPayment[]> {
  const { data } = await api.get<{ payments: ClubPayment[] }>(`/payment-reports/club/${clubId}/payments`);
  return data.payments || [];
}

/**
 * Give money back. The reason is required by the server too — it ends up in the
 * Stripe metadata and in the notification the payer receives.
 */
export async function refundClubPayment(
  clubId: string,
  input: { ledgerId: string; reason: string; amountCents?: number | null }
): Promise<{ refunded_cents: number }> {
  const { data } = await api.post<{ refunded_cents: number }>(`/payment-reports/club/${clubId}/refund`, {
    ledger_id: input.ledgerId,
    reason: input.reason,
    ...(input.amountCents ? { amount_cents: input.amountCents } : {}),
  });
  return data;
}

/** One agreed slice of a period. */
export type Instalment = {
  id: string;
  seq: number;
  count: number;
  due_date: string;
  amount_cents: number;
  paid_cents: number;
  outstanding_cents: number;
  status: string;
  days_until_due?: number;
};

export type ChargePlan = {
  charge: {
    id: string;
    period_label: string;
    due_date: string;
    total_cents: number;
    paid_cents: number;
    outstanding_cents: number;
    currency: string;
    status: string;
  };
  instalments: Instalment[];
  /** What a plan would look like at each number of slices. Empty once one exists. */
  previews: { count: number; amounts: number[]; due_dates: string[] }[];
};

export async function getChargePlan(chargeId: string): Promise<ChargePlan> {
  const { data } = await api.get<ChargePlan>(`/instalments/charge/${chargeId}`);
  return data;
}

export async function createChargePlan(
  chargeId: string,
  input: { count: number; firstDueDate?: string; dueDates?: string[] }
): Promise<{ instalments: number }> {
  const { data } = await api.post<{ instalments: number }>(`/instalments/charge/${chargeId}`, {
    count: input.count,
    ...(input.firstDueDate ? { first_due_date: input.firstDueDate } : {}),
    ...(input.dueDates ? { due_dates: input.dueDates } : {}),
  });
  return data;
}

export async function cancelChargePlan(chargeId: string): Promise<void> {
  await api.delete(`/instalments/charge/${chargeId}`);
}

/** A standing decision not to collect part of a fee, or any of it. */
export type FeeWaiver = {
  id: string;
  athlete_id: string;
  athlete_name: string | null;
  product_id: string | null;
  /** Null means every fee of this club. */
  product_name: string | null;
  kind: "full" | "percent" | "amount" | string;
  percent: number | null;
  amount_cents: number | null;
  starts_on: string;
  ends_on: string | null;
  reason: string;
  granted_by_name: string | null;
  created_at: string;
  revoked_at: string | null;
  /** What it has cost the club so far, across the periods it covered. */
  applied_cents: number;
  applied_periods: number;
};

export async function getClubWaivers(clubId: string): Promise<FeeWaiver[]> {
  const { data } = await api.get<{ waivers: FeeWaiver[] }>(`/waivers/club/${clubId}`);
  return data.waivers || [];
}

export async function grantWaiver(
  clubId: string,
  input: {
    athleteId: string;
    productId?: string | null;
    kind: "full" | "percent" | "amount";
    percent?: number | null;
    amountCents?: number | null;
    startsOn?: string;
    endsOn?: string | null;
    reason: string;
  }
): Promise<{ id: string; repriced: number }> {
  const { data } = await api.post<{ id: string; repriced: number }>(`/waivers/club/${clubId}`, {
    athlete_id: input.athleteId,
    kind: input.kind,
    reason: input.reason,
    ...(input.productId ? { product_id: input.productId } : {}),
    ...(input.percent ? { percent: input.percent } : {}),
    ...(input.amountCents ? { amount_cents: input.amountCents } : {}),
    ...(input.startsOn ? { starts_on: input.startsOn } : {}),
    ...(input.endsOn ? { ends_on: input.endsOn } : {}),
  });
  return data;
}

/** Revoking keeps the record and brings the fee back for unpaid periods only. */
export async function revokeWaiver(clubId: string, id: string): Promise<{ repriced: number }> {
  const { data } = await api.delete<{ repriced: number }>(`/waivers/club/${clubId}/${id}`);
  return data;
}

/**
 * The accounting export, as a file the browser downloads.
 *
 * Fetched through the API client rather than linked directly: the endpoint
 * needs the bearer token, which a plain `<a href>` would not send.
 */
export async function downloadClubPaymentsCsv(clubId: string, from: string, to: string): Promise<void> {
  const { data } = await api.get(`/payment-reports/club/${clubId}/export`, {
    params: { from, to },
    responseType: "blob",
  });
  const url = window.URL.createObjectURL(new Blob([data], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `incasari-${from}_${to}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}
